/* ==========================================================================
   Steven Cali — site JS (vanilla, no dependencies, loaded with `defer`)
   1. (OS 2.0) Shows render server-side from the Shows section's blocks —
      edit dates in the theme editor (Customize → Shows). No array here.
   2. Sticky listen bar (IntersectionObserver on the hero)
   3. Click-to-load facades: Spotify embed + YouTube (youtube-nocookie)
   4. Analytics stub: data-analytics click events with cta_location
   5. Photo slots: hide the styled .img-pending fallback once a photo loads
   6. Scroll reveal: IO-gated, ≤400ms, disabled under prefers-reduced-motion
   7. Product variant picker: selects + JSON variant map (CSP-safe, no
      inline handlers) — updates variant id, price, availability, URL
   8. Cart: per-line quantity changes POST to /cart/change.js (the form's
      Update Cart button stays as the no-JS fallback)
   9. Mobile menu: hamburger + slide-up panel (≤741px) — aria-expanded sync,
      Esc/backdrop/link close, scroll lock, focus management
   ========================================================================== */
(function () {
  "use strict";

  /* JS availability flag — CSS keys mobile-nav behavior off `.js` on <html>
     (hamburger/panel show, in-hero .site-nav fallback row hides). Runs
     first, before anything else, so the styles apply pre-DOMContentLoaded. */
  document.documentElement.classList.add("js");

  var REDUCED_MOTION =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ========================================================================
     1. TOUR CALENDAR — moved out of this file in the OS 2.0 theme.
     ★ STEVEN'S TEAM: shows are now edited in the theme editor —
     Customize → homepage → "Shows" section → add / reorder / remove
     "Show" blocks. Rows render server-side; nothing to edit here. ★
     ======================================================================== */

  /* ========================================================================
     2. STICKY LISTEN BAR — appears once the hero (and its CTA) scrolls out.
     Top of viewport on desktop, fixed bottom thumb-zone ≤741px (CSS),
     hidden entirely in short landscape (CSS media query).
     ======================================================================== */
  function initListenBar() {
    var bar = document.getElementById("listenbar");
    var hero = document.querySelector(".hero");
    if (!bar || !hero) { return; }

    if (!("IntersectionObserver" in window)) {
      bar.classList.add("is-visible"); // old browsers: just show it
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle("is-visible", !entry.isIntersecting);
        });
      },
      { threshold: 0.1 }
    );
    io.observe(hero);

    if (REDUCED_MOTION) {
      bar.style.transition = "none"; // JS guard on top of the CSS gate
    }
  }

  /* Swap a facade's contents for its iframe, then move focus into it so
     keyboard users aren't dropped back to <body> (guarded for old engines). */
  function mountEmbed(container, iframe) {
    if (typeof container.replaceChildren === "function") {
      container.replaceChildren(iframe);
    } else {
      container.textContent = "";
      container.appendChild(iframe);
    }
    iframe.setAttribute("tabindex", "-1");
    iframe.focus();
  }

  /* Append a query param safely (no blind "?x" concat). */
  function withParam(src, key, value) {
    try {
      var u = new URL(src, window.location.href);
      u.searchParams.set(key, value);
      return u.href;
    } catch (err) {
      return src + (src.indexOf("?") === -1 ? "?" : "&") +
        encodeURIComponent(key) + "=" + encodeURIComponent(value);
    }
  }

  /* ========================================================================
     3a. SPOTIFY EMBED FACADE — iframe injected only on click (teaser only).
     152px is pre-reserved in CSS, so zero layout shift.
     ======================================================================== */
  function initSpotifyFacade() {
    var facade = document.getElementById("spotify-facade");
    if (!facade) { return; }
    var btn = facade.querySelector(".embed-btn");
    if (!btn) { return; }
    btn.addEventListener("click", function () {
      var iframe = document.createElement("iframe");
      iframe.src = withParam(btn.getAttribute("data-embed-src") || "", "theme", "0");
      iframe.title = btn.getAttribute("data-embed-title") || "Spotify preview";
      iframe.height = 152;
      iframe.setAttribute(
        "allow",
        "autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      );
      iframe.setAttribute("loading", "lazy");
      mountEmbed(facade, iframe);
      track("load_spotify_embed", "hero_embed", iframe.src);
    });
  }

  /* ========================================================================
     3b. YOUTUBE FACADES — youtube-nocookie iframe injected only on click.
     No third-party JS on page load; protects LCP.
     ======================================================================== */
  function initVideoFacades() {
    var facades = document.querySelectorAll(".video-facade[data-video-id]");
    facades.forEach(function (facade) {
      var btn = facade.querySelector(".video-play");
      if (!btn) { return; }
      btn.addEventListener("click", function () {
        var id = facade.getAttribute("data-video-id");
        var title = facade.getAttribute("data-video-title") || "Video";
        var autoplay = REDUCED_MOTION ? 0 : 1;
        var iframe = document.createElement("iframe");
        iframe.src =
          "https://www.youtube-nocookie.com/embed/" +
          encodeURIComponent(id) +
          "?autoplay=" + autoplay + "&rel=0";
        iframe.title = title;
        iframe.setAttribute(
          "allow",
          "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        );
        iframe.setAttribute("allowfullscreen", "");
        mountEmbed(facade, iframe);
        track("play_video", "videos", id);
      });
    });
  }

  /* ========================================================================
     3c. STREAMS COUNT-UP — hero badge number ([data-countup]) animates
     0 → target over ~1.6s (ease-out cubic, requestAnimationFrame, locale
     thousands separators) and ends on the EXACT server-rendered string
     (e.g. "1,000,000+"). The HTML ships that static string, so no-JS and
     prefers-reduced-motion users just see it — this only ever animates,
     never blanks. Above the fold, so it runs on load (no IO needed).
     ======================================================================== */
  function initCountUp() {
    var els = document.querySelectorAll("[data-countup]");
    if (!els.length || REDUCED_MOTION) { return; }
    if (typeof window.requestAnimationFrame !== "function") { return; }
    els.forEach(function (el) {
      if (el.getAttribute("data-countup-done")) { return; }
      var target = parseInt(el.getAttribute("data-countup"), 10);
      if (isNaN(target) || target <= 0) { return; }
      el.setAttribute("data-countup-done", "1");
      var finalText = el.textContent;
      var suffix = finalText.replace(/[0-9,.\s ]/g, ""); // e.g. "+"
      var duration = 1600;
      var start = null;
      function fmt(n) {
        try { return n.toLocaleString("en-US"); }
        catch (err) { return String(n); }
      }
      function step(ts) {
        if (start === null) { start = ts; }
        var p = Math.min((ts - start) / duration, 1);
        var eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
        if (p < 1) {
          el.textContent = fmt(Math.round(target * eased)) + suffix;
          window.requestAnimationFrame(step);
        } else {
          el.textContent = finalText; // exact final string, always
        }
      }
      el.textContent = fmt(0) + suffix;
      window.requestAnimationFrame(step);
    });
  }

  /* ========================================================================
     4. ANALYTICS STUB — one delegated listener for every [data-analytics]
     element. Pushes to window.dataLayer when present (GA4/GTM) and always
     console.debug's, so events are verifiable before an analytics ID exists.
     To wire GA4 or Plausible, see README.md — no changes needed here.
     ======================================================================== */
  function track(eventName, ctaLocation, targetUrl) {
    var payload = {
      event: eventName,
      cta_location: ctaLocation,
      target_url: targetUrl || ""
    };
    if (window.dataLayer && typeof window.dataLayer.push === "function") {
      window.dataLayer.push(payload);
    }
    if (window.plausible && typeof window.plausible === "function") {
      window.plausible(eventName, { props: { cta_location: ctaLocation } });
    }
    if (window.console && console.debug) {
      console.debug("[analytics]", payload);
    }
  }

  function initAnalytics() {
    document.addEventListener("click", function (e) {
      var el = e.target && e.target.closest
        ? e.target.closest("[data-analytics]")
        : null;
      if (!el) { return; }
      track(
        el.getAttribute("data-analytics"),
        el.getAttribute("data-cta-location") || "unknown",
        el.href || ""
      );
    });
  }

  /* ========================================================================
     5. PHOTO SLOTS — each .img-slot ships with a styled fallback (warm
     gradient + faint SC monogram, class .img-pending). When the real photo
     loads, drop the class so the image shows; on error the fallback simply
     stays (the pending img is opacity:0 in CSS — no broken-image icon).
     ======================================================================== */
  function initImageSlots() {
    var slots = document.querySelectorAll(".img-slot");
    slots.forEach(function (slot) {
      var img = slot.querySelector("img");
      if (!img) { return; }
      function reveal() { slot.classList.remove("img-pending"); }
      if (img.complete) {
        if (img.naturalWidth > 0) { reveal(); } // cached hit; else keep fallback
        return;
      }
      img.addEventListener("load", reveal);
      img.addEventListener("error", function () {
        slot.classList.add("img-missing"); // fallback stays visible
      });
    });
  }

  /* ========================================================================
     6. SCROLL REVEAL — armed only when JS + IntersectionObserver exist and
     the user hasn't asked for reduced motion. Without arming, everything is
     simply visible (no-JS / old browsers / reduced-motion all safe).
     Transition is ≤400ms, fade + 18px translate, runs once per element.
     ======================================================================== */
  function initReveals() {
    var els = document.querySelectorAll(".reveal");
    if (!els.length || REDUCED_MOTION || !("IntersectionObserver" in window)) {
      return;
    }
    document.body.classList.add("reveal-armed");
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach(function (el) { io.observe(el); });
  }

  /* ========================================================================
     7. PRODUCT VARIANT PICKER — one <select> per option + a JSON variant
     map rendered by sections/main-product.liquid. On change: resolve the
     matching variant, update the hidden id input, price, availability and
     the URL (?variant=). CSP-safe: no inline handlers, external JS only.
     ======================================================================== */
  function initVariantPicker() {
    var form = document.querySelector("[data-product-form]");
    if (!form) { return; }
    var jsonEl = form.querySelector("[data-variant-json]");
    var idInput = form.querySelector("[data-variant-id]");
    var selects = form.querySelectorAll(".variant-select");
    if (!jsonEl || !idInput || !selects.length) { return; }

    var variants;
    try { variants = JSON.parse(jsonEl.textContent); } catch (err) { return; }
    if (!variants || !variants.length) { return; }

    var priceEl = document.querySelector("[data-product-price]");
    var addBtn = form.querySelector("[data-add-button]");
    var soldNote = form.querySelector("[data-soldout-note]");

    function selectedOptions() {
      var opts = [];
      selects.forEach(function (sel) {
        opts[parseInt(sel.getAttribute("data-option-index"), 10) || 0] = sel.value;
      });
      return opts;
    }

    function findVariant(opts) {
      for (var i = 0; i < variants.length; i++) {
        var match = true;
        for (var j = 0; j < opts.length; j++) {
          if (variants[i].options[j] !== opts[j]) { match = false; break; }
        }
        if (match) { return variants[i]; }
      }
      return null;
    }

    function update() {
      var variant = findVariant(selectedOptions());
      if (!variant) {
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = "Unavailable";
        }
        return;
      }
      idInput.value = variant.id;
      if (priceEl) {
        if (variant.compare_at_price) {
          priceEl.innerHTML = "";
          priceEl.appendChild(document.createTextNode(variant.price + " "));
          var s = document.createElement("s");
          s.className = "price-compare";
          s.textContent = variant.compare_at_price;
          priceEl.appendChild(s);
        } else {
          priceEl.textContent = variant.price;
        }
      }
      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? "Add to Cart" : "Sold Out";
      }
      if (soldNote) { soldNote.hidden = variant.available; }
      if (window.history && typeof window.history.replaceState === "function") {
        try {
          var u = new URL(window.location.href);
          u.searchParams.set("variant", variant.id);
          window.history.replaceState({}, "", u.href);
        } catch (err) { /* non-blocking */ }
      }
    }

    selects.forEach(function (sel) { sel.addEventListener("change", update); });
  }

  /* ========================================================================
     8. CART QUANTITY — changing a line's qty POSTs to /cart/change.js and
     reloads, so totals always come from Shopify. The form's Update Cart
     button remains the no-JS fallback.
     ======================================================================== */
  function initCartQty() {
    var inputs = document.querySelectorAll("[data-cart-form] .qty-input[data-line-key]");
    if (!inputs.length) { return; }
    inputs.forEach(function (input) {
      input.addEventListener("change", function () {
        var qty = parseInt(input.value, 10);
        if (isNaN(qty) || qty < 0) { return; }
        input.disabled = true;
        fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ id: input.getAttribute("data-line-key"), quantity: qty })
        })
          .then(function (res) {
            if (!res.ok) { throw new Error("cart change failed"); }
            window.location.reload();
          })
          .catch(function () {
            input.disabled = false; // fall back to the Update Cart button
          });
      });
    });
  }

  /* ========================================================================
     9. MOBILE MENU — hamburger + slide-up panel (≤741px; markup in
     sections/topnav.liquid, styles in main.css). Open: slide the panel up,
     sync aria-expanded, lock body scroll (html.menu-open), move focus to
     the first link. Close: X button, any link tap, Esc, or a tap on the
     backdrop (any non-interactive area of the panel); focus returns to the
     hamburger. Null-guarded; a data-menu-bound flag prevents double-binding
     when shopify:section:load re-runs init over the same nodes.
     ======================================================================== */
  function initMobileMenu() {
    var toggle = document.querySelector(".menu-toggle");
    var panel = document.getElementById("mobile-menu");
    if (!toggle || !panel) { return; }
    if (toggle.getAttribute("data-menu-bound")) { return; }
    toggle.setAttribute("data-menu-bound", "1");

    // Editor edge case: if a section reload replaced an OPEN panel, the
    // scroll lock could linger on <html> — clear it on (re-)init.
    document.documentElement.classList.remove("menu-open");

    var closeBtn = panel.querySelector(".menu-close");
    var firstLink = panel.querySelector(".menu-links a") || panel.querySelector("a");

    function isOpen() { return panel.classList.contains("is-open"); }

    function openMenu() {
      panel.classList.add("is-open");
      toggle.setAttribute("aria-expanded", "true");
      document.documentElement.classList.add("menu-open");
      if (firstLink && typeof firstLink.focus === "function") {
        firstLink.focus();
      }
    }

    function closeMenu(returnFocus) {
      panel.classList.remove("is-open");
      toggle.setAttribute("aria-expanded", "false");
      document.documentElement.classList.remove("menu-open");
      if (returnFocus !== false && typeof toggle.focus === "function") {
        toggle.focus();
      }
    }

    toggle.addEventListener("click", function () {
      if (isOpen()) { closeMenu(); } else { openMenu(); }
    });

    if (closeBtn) {
      closeBtn.addEventListener("click", function () { closeMenu(); });
    }

    panel.addEventListener("click", function (e) {
      var t = e.target;
      if (!t || typeof t.closest !== "function") { return; }
      if (t.closest("a")) {
        // Link tap: close and let navigation proceed — no focus steal.
        closeMenu(false);
        return;
      }
      if (!t.closest("button")) {
        closeMenu(); // backdrop tap (non-interactive area of the overlay)
      }
    });

    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape" && e.key !== "Esc") { return; }
      if (isOpen()) { closeMenu(); }
    });
  }

  /* ---- boot ---- */
  initListenBar();
  initSpotifyFacade();
  initVideoFacades();
  initCountUp();
  initAnalytics();
  initImageSlots();
  initReveals();
  initVariantPicker();
  initCartQty();
  initMobileMenu();

  /* Theme editor: re-arm facades/slots/reveals inside a reloaded section. */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener("shopify:section:load", function () {
      initSpotifyFacade();
      initVideoFacades();
      initCountUp();
      initImageSlots();
      initReveals();
      initVariantPicker();
      initCartQty();
      initMobileMenu();
    });
  }
})();
